import { useMemo, useCallback } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  createTypesensePartSearchAdapter,
  searchPartsInTypesense,
  mapTypesenseHitToBackendFormat,
} from "../utils/typesensePartSearch";
import type { TypesenseConfigInput } from "../utils/typesensePartSearch";

export type BackendPartSearchFn = (partNumber: string, page: number) => Promise<any[]>;

export function useInfiniteSearchByPartNumber(
  partNumber: string,
  typesenseConfig: TypesenseConfigInput | null,
  limit = 20,
  backendSearch?: BackendPartSearchFn
) {
  const adapter = useMemo(
    () => (typesenseConfig ? createTypesensePartSearchAdapter(typesenseConfig) : null),
    [typesenseConfig?.host, typesenseConfig?.apiKey]
  );

  const execute = useCallback(
    async (page: number) => {
      if (!partNumber.trim()) return [];
      if (adapter) {
        const hits = await searchPartsInTypesense(
          adapter,
          partNumber.trim(),
          page,
          limit
        );
        return hits.map((h: any) => mapTypesenseHitToBackendFormat(h));
      }
      if (backendSearch) {
        return backendSearch(partNumber.trim(), page);
      }
      return [];
    },
    [adapter, partNumber, limit, backendSearch]
  );

  const isEnabled = (!!adapter || !!backendSearch) && partNumber.trim() !== "";

  const query = useInfiniteQuery({
    queryKey: ["searchPartInfinite", partNumber, !!adapter, !!backendSearch],
    queryFn: ({ pageParam = 1 }: any) => execute(pageParam),
    enabled: isEnabled,
    getNextPageParam: (lastPage: any, allPages: any) =>
      lastPage.length === limit ? allPages.length + 1 : undefined,
  });

  const parts = useMemo(
    () => (query.data?.pages ?? []).reduce<any[]>((acc, p) => acc.concat(p), []),
    [query.data]
  );

  return {
    ...query,
    parts,
    isLoading: query.isInitialLoading,
  };
}
